import React, { useState, useRef, useEffect } from 'react';
import { Layout, Tour } from 'antd';
import { unstable_getServerSession } from 'next-auth/next';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import Editor from '@src/components/scene/editor/Editor';
import { useDisableClicksOnSceneLoad } from '@src/components/hooks/useDisableClicksOnSceneLoad';
import InstructionModal from '@src/components/instruction-modal';
import { NavigationBar } from '@components/navigation-bar/NavigationBar';
import { getSkyboxList } from '@ozoneuniverse/mfe-core/dist/components/skyBox-manager';
import { getAssetPlan } from '@ozoneuniverse/mfe-core/dist/components/managers/AssetsPlan';
import { getSkyboxPreviews } from '@ozoneuniverse/mfe-core/dist/components/skyBox-manager';
import { lightsDefault } from '@ozoneuniverse/mfe-core/dist/components/lights-default';
import { CustomLoader } from '@src/components/scene/canvas-html/customLoader';
import type { TourProps } from 'antd';
import { REDIRECT_TO_STUDIO_EDITOR } from './user/studio/scene/[id]';
import { openNotification } from 'util/helpers';
import { useSceneLoaded } from '@src/components/hooks/useSceneLoaded';
import { useWindowSize } from '@src/components/hooks/useWindowDimensions';
import { useBranding } from '@src/theme/branding/useBranding';
import { useRouter } from 'next/router';

const { Header, Content } = Layout;

export async function getServerSideProps(context) {
  const session = await unstable_getServerSession(
    context.req,
    context.res,
    authOptions
  );
  let isSignIn = false;
  let user = {};

  if (session?.user.id) {
    isSignIn = true;
    user = {
      id: session?.user.id,
      email: session?.user.email,
      role: session?.user.role,
      phone: null,
      bio: null,
      name: null,
      lastname: null,
      walletId: null,
      alias: null,
    };
  }

  const assetsPlan = getAssetPlan({ plans: ['Pack1', 'Pack2', 'Pack3'] });
  const buildSceneOut = Object.keys(assetsPlan)?.reduce((acc, planAct) => {
    const assetAct = assetsPlan[planAct];
    return {
      ...acc,
      [planAct]:
        assetAct?.map((item, index) => {
          const { name, url, preview } = item;
          return {
            name: `Build scene ${name}-${index}`,
            thumbnail: preview,
            url: url,
            nameObject: name,
          };
        }) ?? [],
    };
  }, {});

  const skybox = getSkyboxList('default') || [];
  const skyboxAux = [...skybox];
  skyboxAux.shift();
  const skyboxPreviews = getSkyboxPreviews({
    all: true,
    skyboxPack: 'default',
  });

  const skyboxOut = skyboxPreviews?.map((item: any, index) => {
    return {
      name: `Skybox scene-${index}`,
      thumbnail: item.url,
      object: {
        url: skyboxAux[index].baseUrl,
        fileName: skyboxAux[index].fileName,
        index: index,
      },
    };
  });
  const lightsOut = lightsDefault.map((item, index) => {
    return {
      name: `${item.label}-${index}`,
      thumbnail: item.thumbnail,
      lights: JSON.parse(
        JSON.stringify(
          item.lights.map((lightAct) => {
            return {
              ...lightAct,
              position: lightAct.position
                ? [
                    lightAct.position.x,
                    lightAct.position.y,
                    lightAct.position.z,
                  ]
                : [0, 0, 0],
            };
          })
        )
      ),
      index: index,
    };
  });

  return {
    props: {
      isSignIn,
      user: user,
      email: session?.user.email ?? '',
      userSubscription: [],
      plans: [],
      skyBoxIn: skyboxOut,
      buildScene: buildSceneOut,
      lightsIn: lightsOut,
      fromTemplate: true,
    },
  };
}

export const DEFAULT_SCENE_TEMPLATE = -1;

function Scene({
  isSignIn,
  user,
  email,
  skyBoxIn,
  buildScene,
  lightsIn,
  fromTemplate,
}) {
  const [isSaveScene, setIsSaveScene] = useState(false);
  useDisableClicksOnSceneLoad();
  const { sceneFirstFullyLoaded } = useSceneLoaded();
  const [openTour, setOpenTour] = useState<boolean>(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isExportScene, setIsExportScene] = useState(undefined);
  const [exportSceneProgress, setExportSceneProgress] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const size = useWindowSize();
  const { isZepeto, isUnity, isXsolla } = useBranding();

  const router = useRouter();
  const { id } = router.query;

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);
  const ref6 = useRef(null);
  const ref7 = useRef(null);
  const ref8 = useRef(null);
  const ref9 = useRef(null);
  const ref10 = useRef(null);
  const ref11 = useRef(null);
  const ref12 = useRef(null);
  const ref13 = useRef(null);
  const ref14 = useRef(null);
  const ref15 = useRef(null);
  const ref16 = useRef(null);
  const ref17 = useRef(null);
  const ref18 = useRef(null);

  const baseSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Add to scene',
      description: 'Tab with various objects that can be added to the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
  ];

  const unitySteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Asset store',
      description: 'Tab with 3D assets models for the scene.',
      target: () => ref7.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
  ];

  const zepetoSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'Export as ZepetoWeb',
      description: 'Export your scene as ZepetoWeb package and download.',
      target: () => ref9.current,
    },
    {
      title: 'All scene objects',
      description: 'Tab with all added into the scene objects.',
      target: () => ref5.current,
    },
    {
      title: 'Quick Presets',
      description: 'Tab with World presets for the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Asset store',
      description: 'Tab with 3D assets models for the scene.',
      target: () => ref7.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
  ];

  const xsollaSteps: TourProps['steps'] = [
    {
      title: 'Preview',
      description: 'Open the Scene in preview mode.',
      target: () => ref1.current,
    },
    {
      title: 'Share',
      description: 'Copy link to the scene for share.',
      target: () => ref2.current,
    },
    {
      title: 'Save',
      description: 'Save your changes in the scene.',
      target: () => ref3.current,
    },
    {
      title: 'My projects',
      description: 'Your projects and scenes.',
      target: () => ref9.current,
    },
    {
      title: 'Add to scene',
      description: 'Tab with various objects that can be added to the scene.',
      target: () => ref6.current,
    },
    {
      title: 'Lighting',
      description: 'Tab with Lighting templates for the scene.',
      target: () => ref8.current,
    },
    {
      title: 'Custom Code',
      description: 'Add custom code to your scene.',
      target: () => ref10.current,
    },
    {
      title: 'Sky',
      description: 'Configure sky and environment settings.',
      target: () => ref11.current,
    },
    {
      title: '3D Gen AI',
      description: 'AI-powered 3D generation tools.',
      target: () => ref14.current,
    },
    {
      title: 'Game hub',
      description: 'Game development tools and features.',
      target: () => ref15.current,
    },
    {
      title: 'Enable text chat',
      description: 'Enable text chat and networking features.',
      target: () => ref16.current,
    },
    {
      title: 'Graphics settings',
      description: 'Configure graphics and performance settings.',
      target: () => ref17.current,
    },
  ];

  const steps: TourProps['steps'] = React.useMemo(() => {
    if (isZepeto) {
      return zepetoSteps;
    }

    if (isUnity) {
      return unitySteps;
    }

    if (isXsolla) {
      return xsollaSteps;
    }

    return baseSteps;
  }, [isZepeto, isUnity, isXsolla]);

  const finishTour = () => {
    setOpenTour(false);
    window.localStorage.setItem('not_show_tour', 'true');
  };

  useEffect(() => {
    if (window) {
      const notShowTour = window.localStorage.getItem('not_show_tour');

      if (notShowTour !== 'true' && sceneFirstFullyLoaded) {
        setOpenTour(true);
      }
    }
  }, [sceneFirstFullyLoaded]);

  useEffect(() => {
    function redirectToStudioEditor() {
      if (!id) {
        openNotification(
          'Custom Code',
          'You need to save the scene first to access Custom Code'
        );
        return;
      }

      router.push(`/user/studio/code-editor/${id}`);
    }
    window.addEventListener(REDIRECT_TO_STUDIO_EDITOR, redirectToStudioEditor);

    return () => {
      window.removeEventListener(
        REDIRECT_TO_STUDIO_EDITOR,
        redirectToStudioEditor
      );
    };
  }, []);

  useEffect(() => {
    const handleEditorHasChanges = (event: CustomEvent) => {
      setHasChanges(event.detail.hasChanges);
    };

    window.addEventListener(
      'editor_has_changes',
      handleEditorHasChanges as EventListener
    );

    return () => {
      window.removeEventListener(
        'editor_has_changes',
        handleEditorHasChanges as EventListener
      );
    };
  }, []);

  return (
    <Layout className='layout'>
      <style jsx global>{`
        #__next {
          height: 100%;
        }
        .ant-input-number-handler-wrap {
          opacity: 1;
        }
      `}</style>
      <InstructionModal />
      <Header className='header' style={{ minHeight: 62 }}>
        <NavigationBar
          userEmail={email}
          isSignIn={isSignIn}
          setIsSaveScene={setIsSaveScene}
          setIsExportScene={setIsExportScene}
          isExportScene={isExportScene}
          exportSceneProgress={exportSceneProgress}
          setIsLoginOpen={setIsLoginOpen}
          isLoginOpen={isLoginOpen}
          hasChanges={hasChanges}
          // pass refs directly
          ref1={ref1}
          ref2={ref2}
          ref3={ref3}
          ref4={ref4}
          ref9={ref9}
          setIsTourOpen={setOpenTour}
        />
      </Header>
      <Layout className='layout'>
        <CustomLoader />
        <Content className='content'>
          <Editor
            skyBoxIn={skyBoxIn}
            buildScene={buildScene}
            lightsIn={lightsIn}
            email={email}
            user={user}
            isSaveScene={isSaveScene}
            setIsSaveScene={setIsSaveScene}
            isExportScene={isExportScene}
            setIsExportScene={setIsExportScene}
            setExportSceneProgress={setExportSceneProgress}
            isSignIn={isSignIn}
            fromTemplate={fromTemplate}
            setIsLoginOpen={setIsLoginOpen as any}
            isLoginOpen={isLoginOpen}
            tourRefs={[ref5, ref6, ref7, ref8, null, null, ref6, null, ref8]}
            setIsTourOpen={setOpenTour}
            setHasChanges={setHasChanges}
          />
        </Content>
      </Layout>
      <Tour open={openTour} onClose={finishTour} steps={steps} />
    </Layout>
  );
}
export default Scene;
